import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  UserGroupIcon,
  MapPinIcon,
  CheckCircleIcon,
  SparklesIcon,
  PlayIcon,
  MusicalNoteIcon,
  PhotoIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  StarIcon,
  FireIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import Modal, { ModalContent } from '../../../components/ui/Modal';
import { creatorService } from '../../../services/creatorService';
import { paymentsService } from '../../../services/paymentsService';

interface Creator {
  id: string;
  user: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  displayName: string;
  description?: string;
  category?: string;
  coverImage?: string;
  location?: string;
  isPublic: boolean;
  isVerified?: boolean;
  tiers: Tier[];
  _count?: {
    tiers: number;
    memberships: number;
  };
}

interface Tier {
  id: string;
  name: string;
  description?: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  currentPatrons: number;
  maxPatrons?: number;
  freeTrialDays?: number;
}

const DiscoverPage: React.FC = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [featuredCreators, setFeaturedCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'az'>('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [joiningTierId, setJoiningTierId] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: 'All Categories', icon: SparklesIcon },
    { id: 'art', name: 'Art & Illustration', icon: PhotoIcon },
    { id: 'music', name: 'Music & Audio', icon: MusicalNoteIcon },
    { id: 'video', name: 'Video & Film', icon: VideoCameraIcon },
    { id: 'writing', name: 'Writing & Publishing', icon: DocumentTextIcon },
    { id: 'games', name: 'Games & Development', icon: PlayIcon },
  ];

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load creators when filters change
  useEffect(() => {
    loadCreators();
  }, [debouncedSearch, selectedCategory, sortBy]);

  // Load featured creators on mount
  useEffect(() => {
    loadFeaturedCreators();
  }, []);

  const loadCreators = async () => {
    setLoading(true);
    try {
      const params: any = {
        query: debouncedSearch || undefined,
        limit: 12,
        offset: 0,
        sort: sortBy,
      };

      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const response = await creatorService.searchCreators(params);
      setCreators(response.creators || []);
    } catch (error) {
      console.error('Failed to load creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedCreators = async () => {
    try {
      // Load top 4 popular creators as "featured"
      const response = await creatorService.searchCreators({
        query: '',
        limit: 4,
        offset: 0,
        sort: 'popular',
      });
      setFeaturedCreators(response.creators || []);
    } catch (error) {
      console.error('Failed to load featured creators:', error);
    }
  };

  const handleJoinCreator = async (creatorId: string, tierId: string) => {
    if (joiningTierId) return; // Prevent double-clicks

    setJoiningTierId(tierId);
    try {
      const response = await paymentsService.createCheckoutSession(tierId);
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      alert(error?.response?.data?.message || 'Failed to start checkout. Please try again.');
      setJoiningTierId(null);
    }
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    const Icon = category?.icon || SparklesIcon;
    return <Icon className="w-4 h-4" />;
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getCreatorName = (creator: Creator) => {
    return creator.displayName || 
           (creator.user.firstName && creator.user.lastName 
             ? `${creator.user.firstName} ${creator.user.lastName}` 
             : creator.user.username);
  };

  const getCreatorAvatar = (creator: Creator) => {
    return creator.user.avatar || creator.coverImage;
  };

  if (loading && creators.length === 0 && featuredCreators.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-12 bg-gray-200 rounded w-full mb-8"></div>
          
          {/* Featured skeleton */}
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-80 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          
          {/* Results skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-96 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Discover Creators</h1>
        <p className="text-gray-600 mt-2">
          Find and support your favorite creators with exclusive content and benefits
        </p>
      </div>

      {/* Featured Creators Section */}
      {featuredCreators.length > 0 && !searchTerm && selectedCategory === 'all' && (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <StarIcon className="w-6 h-6 mr-2" style={{ color: 'var(--patreon-orange)' }} />
              Featured Creators
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredCreators.map((creator, index) => (
              <motion.div
                key={creator.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="hover:shadow-2xl hover:scale-105 transition-all duration-300 group cursor-pointer" onClick={() => navigate(`/creators/${creator.user.username}`)}>
                  <CardContent className="p-0">
                  {/* Cover/Banner */}
                  <div className="h-32 bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 relative overflow-hidden">
                    {creator.coverImage && (
                      <img src={creator.coverImage} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute top-2 right-2">
                      <span className="bg-white/90 backdrop-blur px-2 py-1 rounded-full text-xs font-semibold flex items-center" style={{ color: 'var(--patreon-orange)' }}>
                        <FireIcon className="w-3 h-3 mr-1" />
                        Featured
                      </span>
                    </div>
                  </div>
                  
                  {/* Avatar */}
                  <div className="px-4 -mt-10 relative z-10">
                    <div className="w-20 h-20 rounded-full border-4 border-white bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
                      {getCreatorAvatar(creator) ? (
                        <img src={getCreatorAvatar(creator)} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        getCreatorName(creator).charAt(0)
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-4 pb-4 mt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 text-lg">{getCreatorName(creator)}</h3>
                      {creator.isVerified && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {creator.description || 'No description available'}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                      <span className="flex items-center">
                        <UserGroupIcon className="w-4 h-4 mr-1" />
                        {creator._count?.memberships || 0} patrons
                      </span>
                      {creator.tiers.length > 0 && (
                        <span className="font-semibold" style={{ color: 'var(--patreon-orange)' }}>
                          From {formatCurrency(Math.min(...creator.tiers.map(t => t.amount)))}/mo
                        </span>
                      )}
                    </div>

                    <Button size="sm" className="w-full group-hover:scale-105 transition-transform">
                      View Page <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search creators, categories, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-all"
              style={{
                borderColor: searchTerm ? 'var(--patreon-orange)' : '',
                boxShadow: searchTerm ? '0 0 0 3px rgba(255, 107, 53, 0.1)' : '',
              }}
            />
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none"
            >
              <option value="popular">Most Popular</option>
              <option value="newest">Newest</option>
              <option value="az">A-Z</option>
            </select>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<AdjustmentsHorizontalIcon className="w-5 h-5" />}
            >
              Filters
            </Button>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-all flex items-center ${
                  selectedCategory === category.id
                    ? 'border-2 border-orange-300 text-orange-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                }`}
                style={selectedCategory === category.id ? { backgroundColor: 'var(--patreon-cream)' } : {}}
              >
                <Icon className="w-4 h-4 mr-1" />
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Advanced Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price Range: {formatCurrency(priceRange.min)} - {formatCurrency(priceRange.max)}
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: parseInt(e.target.value) }))}
                      className="flex-1"
                    />
                    <input
                      type="range"
                      min="0"
                      max="1000"
                      step="10"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: parseInt(e.target.value) }))}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPriceRange({ min: 0, max: 1000 });
                    setSelectedCategory('all');
                    setSearchTerm('');
                  }}
                >
                  Clear Filters
                </Button>
                <span className="text-sm text-gray-600">
                  {creators.length} creators found
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse">
              <Card>
                <CardContent className="p-6">
                  <div className="h-64 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      ) : creators.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No creators found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search terms or filters to find more creators.
            </p>
            <Button
              onClick={() => {
                setPriceRange({ min: 0, max: 1000 });
                setSelectedCategory('all');
                setSearchTerm('');
              }}
            >
              Clear All Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {creators.map((creator, index) => (
            <motion.div
              key={creator.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="hover:shadow-2xl hover:border-purple-200 transition-all duration-300">
                <CardContent className="p-6">
                {/* Creator Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {getCreatorAvatar(creator) ? (
                        <img src={getCreatorAvatar(creator)} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        getCreatorName(creator).charAt(0)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900 truncate">{getCreatorName(creator)}</h3>
                        {creator.isVerified && (
                          <CheckCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0" title="Verified Creator" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">@{creator.user.username}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedCreator(creator)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <SparklesIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {creator.description || 'No description available'}
                </p>

                {/* Category */}
                {creator.category && (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                      {getCategoryIcon(creator.category)}
                      <span className="ml-1 capitalize">{creator.category}</span>
                    </span>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <UserGroupIcon className="w-4 h-4 mr-1" />
                    <span>{creator._count?.memberships || 0} patrons</span>
                  </div>
                  {creator.location && (
                    <div className="flex items-center text-gray-500 truncate">
                      <MapPinIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{creator.location}</span>
                    </div>
                  )}
                </div>

                {/* Tiers */}
                {creator.tiers.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Membership Tiers</span>
                      <span className="text-xs text-gray-500">From {formatCurrency(Math.min(...creator.tiers.map(t => t.amount)))}/month</span>
                    </div>

                    <div className="space-y-2">
                      {creator.tiers.slice(0, 2).map((tier) => (
                        <div key={tier.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 truncate">{tier.name}</p>
                              {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                                <span className="text-xs font-semibold text-green-600 flex-shrink-0">🎁 {tier.freeTrialDays}d trial</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600">{tier.currentPatrons} patrons</p>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-2">
                            {formatCurrency(tier.amount)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {creator.tiers.length > 2 && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        +{creator.tiers.length - 2} more tier{creator.tiers.length - 2 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/creators/${creator.user.username}`)}
                  >
                    View Page
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => setSelectedCreator(creator)}
                  >
                    Join Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          ))}
        </div>
      )}

      {/* Creator Detail Modal (Quick Preview) */}
      {selectedCreator && (
        <Modal
          isOpen={!!selectedCreator}
          onClose={() => setSelectedCreator(null)}
          title={getCreatorName(selectedCreator)}
          size="2xl"
        >
          <ModalContent>
            <div className="space-y-6">
              {/* Creator Info */}
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-xl flex-shrink-0">
                  {getCreatorAvatar(selectedCreator) ? (
                    <img src={getCreatorAvatar(selectedCreator)} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    getCreatorName(selectedCreator).charAt(0)
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h2 className="text-xl font-semibold text-gray-900">{getCreatorName(selectedCreator)}</h2>
                    {selectedCreator.isVerified && (
                      <CheckCircleIcon className="w-5 h-5 text-blue-500" title="Verified Creator" />
                    )}
                  </div>
                  <p className="text-gray-600 mb-3">{selectedCreator.description || 'No description available'}</p>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <UserGroupIcon className="w-4 h-4 mr-1" />
                      {selectedCreator._count?.memberships || 0} patrons
                    </span>
                    {selectedCreator.location && (
                      <span className="flex items-center">
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        {selectedCreator.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tiers */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Choose Your Membership</h3>
                <div className="space-y-4">
                  {selectedCreator.tiers.map((tier) => (
                    <div key={tier.id} className="border rounded-lg p-4 hover:border-orange-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-900">{tier.name}</h4>
                            {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                🎁 {tier.freeTrialDays}-day trial
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{tier.description || 'No description'}</p>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className="text-xl font-semibold text-gray-900">{formatCurrency(tier.amount)}</p>
                          <p className="text-sm text-gray-600">/month</p>
                        </div>
                      </div>

                      {tier.benefits.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Benefits:</p>
                          <ul className="space-y-1">
                            {tier.benefits.map((benefit, index) => (
                              <li key={index} className="flex items-start text-sm text-gray-600">
                                <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {tier.currentPatrons} patron{tier.currentPatrons !== 1 ? 's' : ''}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => handleJoinCreator(selectedCreator.id, tier.id)}
                          disabled={joiningTierId === tier.id}
                        >
                          {joiningTierId === tier.id ? 'Processing...' : 'Choose This Tier'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* View Full Page */}
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/creators/${selectedCreator.user.username}`)}
                >
                  View Full Creator Page <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default DiscoverPage;
